import * as React from "react";
import { cn } from "../lib/utils";

export interface ContextWindowRingProps {
  /** Total context window usage percentage (0–100) */
  percentage: number;
  /** Input tokens as percentage of max (0–100) */
  inputPercentage?: number;
  /** Output tokens as percentage of max (0–100) */
  outputPercentage?: number;
  /** Ring diameter in pixels */
  size?: number;
  /** Stroke width in pixels */
  strokeWidth?: number;
  className?: string;
  children?: React.ReactNode;
}

/**
 * SVG circular progress ring inspired by Google Fit.
 *
 * Supports concentric rings:
 * - Outer track: context window fill
 * - Inner track: input/output split
 */
export const ContextWindowRing = React.forwardRef<
  HTMLDivElement,
  ContextWindowRingProps
>(
  (
    {
      percentage,
      inputPercentage = 0,
      outputPercentage = 0,
      size = 64,
      strokeWidth = 4,
      className,
      children,
    },
    ref
  ) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const innerRadius = Math.max(0, radius - strokeWidth * 1.8);
    const innerCircumference = 2 * Math.PI * innerRadius;

    const clampedPct = Math.min(100, Math.max(0, percentage));
    const clampedInput = Math.min(100, Math.max(0, inputPercentage));
    const clampedOutput = Math.min(100, Math.max(0, outputPercentage));

    const dashOffset = circumference - (clampedPct / 100) * circumference;
    const inputOffset =
      innerCircumference - (clampedInput / 100) * innerCircumference;
    const outputOffset =
      innerCircumference - (clampedOutput / 100) * innerCircumference;

    const center = size / 2;

    return (
      <div
        ref={ref}
        className={cn("relative inline-flex items-center justify-center", className)}
        style={{ width: size, height: size }}
      >
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90"
        >
          {/* Background tracks */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            className="stroke-muted"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            fill="none"
            className="stroke-muted/50"
            strokeWidth={strokeWidth * 0.75}
          />

          {/* Input ring (inner) */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            fill="none"
            stroke="hsl(var(--chart-1))"
            strokeWidth={strokeWidth * 0.75}
            strokeDasharray={innerCircumference}
            strokeDashoffset={inputOffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />

          {/* Output ring (inner, offset by input) */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            fill="none"
            stroke="hsl(var(--chart-2))"
            strokeWidth={strokeWidth * 0.75}
            strokeDasharray={innerCircumference}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
            style={{
              strokeDashoffset:
                innerCircumference -
                (Math.min(100, clampedInput + clampedOutput) / 100) * innerCircumference,
            }}
          />

          {/* Total usage ring (outer) */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            strokeLinecap="round"
            className="transition-all duration-500 ease-out"
          />
        </svg>

        {/* Center content */}
        {children && (
          <div className="absolute inset-0 flex items-center justify-center">
            {children}
          </div>
        )}
      </div>
    );
  }
);
ContextWindowRing.displayName = "ContextWindowRing";
